import { useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StarDisplay } from '@/components/common/StarRating';
import { cn } from '@/lib/utils';
import type { IProjectMake } from './projectMake.d';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface RatingButtonProps {
  averageTotalRating?: number;
  makes?: IProjectMake[];
  /** Rated-make count from the feed, where full makes aren't fetched. */
  totalMakes?: number;
  isLoading?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

function RatingButton({
  averageTotalRating,
  makes,
  totalMakes,
  isLoading,
  size = 'sm',
  className,
}: RatingButtonProps) {
  const [open, setOpen] = useState(false);
  const rating = averageTotalRating ?? 0;

  const hasMakes = Array.isArray(makes) && makes.length > 0;
  const ratingCategories = [
    'cutExpectation',
    'cutQuality',
    'correctFileInfo',
    'coolFactor',
  ] as const;
  const makesWithRatings = (makes || []).filter(
    (m) => m.isPrinted && m.ratings
  );
  const ratingAverages = ratingCategories.reduce(
    (acc, category) => {
      const values = makesWithRatings
        .map((m) => m.ratings?.[category] ?? 0)
        .filter((v) => typeof v === 'number' && v > 0);
      const avg =
        values.length > 0
          ? Math.round(
              (values.reduce((a, b) => a + b, 0) / values.length) * 10
            ) / 10
          : 0;
      acc[category] = { avg, count: values.length };
      return acc;
    },
    {} as Record<
      (typeof ratingCategories)[number],
      { avg: number; count: number }
    >
  );
  const totalRatings = makes ? makesWithRatings.length : (totalMakes ?? 0);

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size={size}
              className={cn(
                'flex items-center gap-2 justify-center text-[13px]',
                className
              )}
              onClick={hasMakes ? () => setOpen(true) : undefined}
              style={hasMakes ? {} : { pointerEvents: 'none' }}
              title={hasMakes ? 'Show Ratings Distribution' : undefined}
              disabled={isLoading}
            >
              <Star size={16} fill="#FFD700" stroke="#FFD700" />
              <span>{totalRatings > 0 ? rating : '—'}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {totalRatings > 0
              ? `${rating} average from ${totalRatings} ${totalRatings === 1 ? 'make' : 'makes'}`
              : 'No ratings yet'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {hasMakes && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md bg-gray-50 rounded-2xl shadow-xl border border-gray-200">
            <DialogHeader>
              <DialogTitle>Ratings Distribution</DialogTitle>
              <DialogDescription className="text-gray-600 mb-2">
                Average rating for each category across all makes for this post.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 p-1">
              <div className="font-semibold text-base text-center text-gray-800">
                Total Ratings:{' '}
                <span className="text-primary font-bold">
                  {totalRatings} of {makes?.length ?? 0}
                </span>{' '}
                makes
              </div>
              <Separator className="my-2" />
              <div className="grid gap-3">
                {ratingCategories.map((category) => (
                  <div
                    key={category}
                    className="flex items-center justify-between bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-100"
                  >
                    <span className="font-semibold text-gray-700 text-left w-40 capitalize">
                      {category.replace(/([A-Z])/g, ' $1')}
                    </span>
                    <StarDisplay
                      rating={ratingAverages[category].avg}
                      size="lg"
                      showValue
                    />
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                className="w-full"
              >
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

export default RatingButton;
